import { Bell, Flame, Info } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { type FC, useState } from 'react';
import classNames from 'classnames';

import type { MenuNotification, MenuNotificationType } from '../../types/menu-notifications';

const iconForType = (type: MenuNotificationType) => {
  switch (type) {
    case 'hot-offer':
      return <Flame className="h-4 w-4" strokeWidth={2} />;
    case 'update':
      return <Info className="h-4 w-4" strokeWidth={2} />;
    case 'news':
    default:
      return <Bell className="h-4 w-4" strokeWidth={2} />;
  }
};

const colorForType = (type: MenuNotificationType) => {
  switch (type) {
    case 'hot-offer':
      return 'text-orange-500';
    case 'update':
      return 'text-green-500';
    case 'news':
    default:
      return 'text-blue-500';
  }
};

/** Как в figma: TooltipContent `max-w-sm` (24rem) */
const NOTIFICATION_PANEL_CLASS =
  'absolute left-full top-1/2 z-[100] ml-2 max-w-sm w-[min(100vw-2rem,24rem)] -translate-y-1/2 rounded-lg border border-gray-200 bg-white p-4 shadow-xl';
const NOTIFICATION_BRIDGE_CLASS = 'absolute left-full top-0 z-[99] h-full w-2';

interface MenuNotificationTrailingProps {
  notifications: MenuNotification[];
  /** Активный пункт меню (красный фон) — иконки белые */
  isActive: boolean;
}

/**
 * Правая часть пункта меню: иконка типа первого уведомления, бейдж-счётчик, панель при наведении.
 * Логика как в figma code MenuItemWithNotification.
 */
export const MenuNotificationTrailing: FC<MenuNotificationTrailingProps> = ({
  notifications,
  isActive,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const hasNotifications = notifications.length > 0;

  if (!hasNotifications) {
    return null;
  }

  const first = notifications[0];

  return (
    <div
      className="relative flex shrink-0 items-center gap-2"
      onMouseEnter={() => setPanelOpen(true)}
      onMouseLeave={() => setPanelOpen(false)}
    >
      <motion.div
        className="relative flex items-center justify-center"
        animate={shouldReduceMotion ? undefined : { scale: [1, 1.08, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className={classNames(isActive ? 'text-white' : colorForType(first.type))}>
          {iconForType(first.type)}
        </span>
        <motion.span
          className={classNames(
            'pointer-events-none absolute inset-0 rounded-full opacity-30',
            isActive ? 'bg-white' : colorForType(first.type),
          )}
          animate={shouldReduceMotion ? undefined : { scale: [1, 1.5, 1], opacity: [0.25, 0, 0.25] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        {notifications.length > 1 && (
          <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white">
            {notifications.length}
          </span>
        )}
      </motion.div>

      {panelOpen && (
        <>
          <div className={NOTIFICATION_BRIDGE_CLASS} aria-hidden />
          <div className={NOTIFICATION_PANEL_CLASS} role="tooltip">
            <div className="max-h-[min(70vh,360px)] space-y-3 overflow-y-auto">
              {notifications.map((n) => (
                <div key={n.id} className="space-y-1.5 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-2">
                    <span className={classNames('mt-0.5 shrink-0', colorForType(n.type))}>{iconForType(n.type)}</span>
                    <h4 className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900">{n.title}</h4>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-600">{n.message}</p>
                  {n.link ? (
                    <a
                      href={n.link}
                      className="inline-block text-xs text-blue-600 hover:underline"
                      onClick={(e) => n.link === '#' && e.preventDefault()}
                    >
                      {n.actionLabel || 'Подробнее →'}
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Колокольчик для пункта, где нужна только иконка «есть уведомления» (напр. Инструменты), без смены на flame */
/**
 * Только иконка и счётчик (без панели) — для мобильного drawer, где hover недоступен.
 */
export const MenuNotificationCompact: FC<{
  notifications: MenuNotification[];
  /** `bell` — колокольчик как у «Инструменты»; `typed` — иконка по типу первого уведомления */
  variant: 'bell' | 'typed';
  isActive?: boolean;
}> = ({ notifications, variant, isActive }) => {
  if (notifications.length === 0) return null;
  const first = notifications[0];
  const icon =
    variant === 'bell' ? (
      <Bell className={classNames('h-4 w-4', isActive ? 'text-white' : 'text-gray-500')} strokeWidth={2} />
    ) : (
      <span className={classNames(isActive ? 'text-white' : colorForType(first.type))}>{iconForType(first.type)}</span>
    );
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      {icon}
      {notifications.length > 1 && (
        <span className="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold leading-none text-white">
          {notifications.length}
        </span>
      )}
    </span>
  );
};

export const MenuBellTrailing: FC<{ isActive: boolean; notifications: MenuNotification[] }> = ({
  isActive,
  notifications,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  if (notifications.length === 0) return null;

  return (
    <div
      className="relative flex shrink-0 items-center"
      onMouseEnter={() => setPanelOpen(true)}
      onMouseLeave={() => setPanelOpen(false)}
    >
      <motion.div
        className="relative flex items-center justify-center"
        animate={shouldReduceMotion ? undefined : { scale: [1, 1.08, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Bell className={classNames('h-4 w-4', isActive ? 'text-white' : 'text-gray-500')} strokeWidth={2} />
        {notifications.length > 1 && (
          <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white">
            {notifications.length}
          </span>
        )}
      </motion.div>

      {panelOpen && (
        <>
          <div className={NOTIFICATION_BRIDGE_CLASS} aria-hidden />
          <div className={NOTIFICATION_PANEL_CLASS}>
            <div className="max-h-[min(70vh,360px)] space-y-3 overflow-y-auto">
              {notifications.map((n) => (
                <div key={n.id} className="space-y-1.5 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-2">
                    <span className={classNames('mt-0.5 shrink-0', colorForType(n.type))}>{iconForType(n.type)}</span>
                    <h4 className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900">{n.title}</h4>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-600">{n.message}</p>
                  {n.link ? (
                    <a
                      href={n.link}
                      className="inline-block text-xs text-blue-600 hover:underline"
                      onClick={(e) => n.link === '#' && e.preventDefault()}
                    >
                      {n.actionLabel || 'Подробнее →'}
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
